// Constants + helpers shared between the MessageSentReceivedDetailReport
// page and the dashboard origin tiles that drill into it
// (Communication-by-Source, Communication-by-Sales-Person, "View detailed
// report"). Centralized here so renames / new tokens / new columns flow
// to every consumer from one place.

export const TABS = { SENT: "sent", RECEIVED: "received" };
export const SEGMENTS = { TEXT: "text", EMAIL: "email", CALL: "call" };

export const FILTER_BOX_KEYS = {
  OTTO: "otto",
  AI: "ai",
  AI_EDITED: "aiEdited",
  MANUAL: "manual",
};

// Backend payload values (direction / channel / sendType) keyed by the
// FE token they correspond to.
export const DIRECTION = { [TABS.SENT]: "SENT", [TABS.RECEIVED]: "RECEIVED" };
export const CHANNEL = {
  [SEGMENTS.TEXT]: "TEXT",
  [SEGMENTS.EMAIL]: "EMAIL",
  [SEGMENTS.CALL]: "CALL",
};

// FilterBox catalog — order here = render order on the page. Only
// applies to TEXT/EMAIL SENT.
export const FILTER_BOXES = [
  {
    key: FILTER_BOX_KEYS.OTTO,
    titleKey: "filterBoxes.otto",
    sendType: "OTTO",
    summaryKey: "otto",
  },
  {
    key: FILTER_BOX_KEYS.AI,
    titleKey: "filterBoxes.aiAssisted",
    sendType: "AI_ASSISTED",
    summaryKey: "aiAssisted",
  },
  {
    key: FILTER_BOX_KEYS.AI_EDITED,
    titleKey: "filterBoxes.aiAssistedEdited",
    sendType: "AI_ASSISTED_EDITED",
    summaryKey: "aiAssistedEdited",
  },
  {
    key: FILTER_BOX_KEYS.MANUAL,
    titleKey: "filterBoxes.manual",
    sendType: "MANUAL",
    summaryKey: "manual",
  },
];

// Module key consumed via persistedFilters — origin tiles dispatch a
// one-shot preset under this key (matches MediaTrackingDetailReport /
// ReviewDetailReport handoff pattern).
export const MESSAGE_DETAIL_MODULE = "messageSentReceivedDetailReport";

// `source` URL param values — drive the page header copy only.
export const MESSAGE_REPORT_SOURCE = {
  SENT_VS_RECEIVED: "messages_sent_vs_received",
  BY_SALES_PERSON: "communication_by_sales_person",
  BY_SOURCE: "communication_by_source",
};

export const HEADER_BY_SOURCE = {
  [MESSAGE_REPORT_SOURCE.SENT_VS_RECEIVED]: "Messages Sent vs. Received",
  [MESSAGE_REPORT_SOURCE.BY_SALES_PERSON]: "Communication by Sales Person",
  [MESSAGE_REPORT_SOURCE.BY_SOURCE]: "Communication by Source",
};

// Substrings the BE uses inside metric names (e.g. `text_otto_sent`,
// `email_received`, `call_inbound`). Update here if the BE renames a
// channel / send-type and the metric-name resolver picks it up.
export const METRIC_TOKENS = {
  TEXT: "text",
  EMAIL: "email",
  CALL: "call",
  RECEIVED: "received",
  INBOUND: "inbound",
  OTTO: "otto",
  MANUAL: "manual",
  AI_ASSISTED: "ai_assisted",
  AI_ASSISTED_EDITED: "ai_assisted_edited",
};

// Communication-by-Sales-Person column `dataIndex` (camelCase) → detail
// page selection. Add a row here when a new leaf column is introduced.
export const SALES_MSG_COL_TO_DETAIL = {
  textOttoSent: { tab: TABS.SENT, segment: SEGMENTS.TEXT, filterBox: FILTER_BOX_KEYS.OTTO },
  textAiAssistedSent: { tab: TABS.SENT, segment: SEGMENTS.TEXT, filterBox: FILTER_BOX_KEYS.AI },
  textManualSent: { tab: TABS.SENT, segment: SEGMENTS.TEXT, filterBox: FILTER_BOX_KEYS.MANUAL },
  textReceived: { tab: TABS.RECEIVED, segment: SEGMENTS.TEXT, filterBox: null },
  emailOttoSent: { tab: TABS.SENT, segment: SEGMENTS.EMAIL, filterBox: FILTER_BOX_KEYS.OTTO },
  emailAiAssistedSent: { tab: TABS.SENT, segment: SEGMENTS.EMAIL, filterBox: FILTER_BOX_KEYS.AI },
  emailManualSent: { tab: TABS.SENT, segment: SEGMENTS.EMAIL, filterBox: FILTER_BOX_KEYS.MANUAL },
  emailReceived: { tab: TABS.RECEIVED, segment: SEGMENTS.EMAIL, filterBox: null },
  callInbound: { tab: TABS.RECEIVED, segment: SEGMENTS.CALL, filterBox: null },
  callOutbound: { tab: TABS.SENT, segment: SEGMENTS.CALL, filterBox: null },
};

// Parse a BE metric name (snake_case / Title Case / camelCase variants)
// into the detail-page selection. Used by Communication-by-Source cell
// clicks where leaf columns are dynamic and the dataIndex map above
// can't be used.
export const resolveDetailSelectionFromMetric = metricName => {
  const key = (metricName || "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/\s+/g, "_")
    .toLowerCase();

  const hasChannelToken = token => new RegExp(`(^|_)${token}(_|$)`).test(key);
  let segment = null;
  if (hasChannelToken(METRIC_TOKENS.TEXT)) segment = SEGMENTS.TEXT;
  else if (hasChannelToken(METRIC_TOKENS.EMAIL)) segment = SEGMENTS.EMAIL;
  else if (hasChannelToken(METRIC_TOKENS.CALL)) segment = SEGMENTS.CALL;

  const tab =
    key.includes(METRIC_TOKENS.RECEIVED) || key.includes(METRIC_TOKENS.INBOUND)
      ? TABS.RECEIVED
      : TABS.SENT;

  let filterBox = null;
  if (tab === TABS.SENT && (segment === SEGMENTS.TEXT || segment === SEGMENTS.EMAIL)) {
    if (key.includes(METRIC_TOKENS.AI_ASSISTED_EDITED)) filterBox = FILTER_BOX_KEYS.AI_EDITED;
    else if (key.includes(METRIC_TOKENS.AI_ASSISTED)) filterBox = FILTER_BOX_KEYS.AI;
    else if (key.includes(METRIC_TOKENS.OTTO)) filterBox = FILTER_BOX_KEYS.OTTO;
    else if (key.includes(METRIC_TOKENS.MANUAL)) filterBox = FILTER_BOX_KEYS.MANUAL;
  }
  return { tab, segment, filterBox };
};
